import { yupResolver } from "@hookform/resolvers/yup";
import { Stack } from "@mui/material";
import { FufiContributionType, FufiPaymentType } from "@repo/api";
import { type FC, useEffect } from "react";
import { FormProvider, useForm } from "react-hook-form";
import * as yup from "yup";
import { useTypedTranslation } from "../../hooks";
import { CheckboxInput, DatePicker } from "../Form";
import * as Styled from "./TransactionsTab.styled";
import { PaymentsOverviewFilter } from "./TransactionsTabPaymentsOverview";

type TransactionsTabPaymentsOverviewFilterModalFormValues = {
    from: Date | null;
    to: Date | null;
    payerEmployee: boolean | null;
    payerEmployer: boolean | null;
    paymentTypeRegular: boolean | null;
    paymentTypeExtra: boolean | null;
};

export const TransactionsTabPaymentsOverviewFilterModal: FC<{
    setReturnPayload: React.Dispatch<React.SetStateAction<object | undefined>>;
    resolve?: () => void;
    initialValues: PaymentsOverviewFilter;
}> = ({ setReturnPayload, initialValues }) => {
    const { t } = useTypedTranslation();

    const validationSchema = yup
        .object<TransactionsTabPaymentsOverviewFilterModalFormValues>()
        .shape({
            from: yup.date().typeError(t("errorMessages.invalidDateFormat")).nullable(),
            to: yup.date().typeError(t("errorMessages.invalidDateFormat")).nullable(),
            payerEmployee: yup.boolean().nullable(),
            payerEmployer: yup.boolean().nullable(),
            paymentTypeRegular: yup.boolean().nullable(),
            paymentTypeExtra: yup.boolean().nullable(),
        })
        .test("date-range", t("errorMessages.toDateLaterThanFromDate"), function (values, ctx) {
            const { from, to } = values || {};
            if (from && to && from > to) {
                return ctx.createError({
                    path: "from",
                    message: t("errorMessages.toDateLaterThanFromDate"),
                });
            }
            return true;
        });

    const methods = useForm({
        defaultValues: {
            from: initialValues.from ?? null,
            to: initialValues.to ?? null,
            payerEmployee: initialValues.moneyInsPayerTypes?.includes(FufiContributionType.Employee) ?? null,
            payerEmployer: initialValues.moneyInsPayerTypes?.includes(FufiContributionType.Employer) ?? null,
            paymentTypeRegular: initialValues.moneyInsPaymentTypes?.includes(FufiPaymentType.Normal) ?? null,
            paymentTypeExtra: initialValues.moneyInsPaymentTypes?.includes(FufiPaymentType.Unsolicited) ?? null,
        },
        mode: "onChange",
        resolver: yupResolver(validationSchema),
    });
    const { watch, trigger } = methods;

    const userDefinedFilter = watch();

    useEffect(() => {
        // Revalidate both fields so cross-field errors clear correctly
        void trigger(["from", "to"]);

        const newFilter: PaymentsOverviewFilter = {
            from: userDefinedFilter.from || null,
            to: userDefinedFilter.to || null,
            moneyInsPayerTypes: [
                userDefinedFilter.payerEmployee ? FufiContributionType.Employee : null,
                userDefinedFilter.payerEmployer ? FufiContributionType.Employer : null,
            ].filter(Boolean) as FufiContributionType[],
            moneyInsPaymentTypes: [
                userDefinedFilter.paymentTypeRegular ? FufiPaymentType.Normal : null,
                userDefinedFilter.paymentTypeExtra ? FufiPaymentType.Unsolicited : null,
            ].filter(Boolean) as FufiPaymentType[],
        };

        setReturnPayload(newFilter);
    }, [userDefinedFilter, setReturnPayload, trigger]);

    return (
        <FormProvider {...methods}>
            <form>
                <Styled.VerticalStackWithGap>
                    <Stack>
                        <Styled.FilterFormLabel>{t("fufi.regulationsFilters.payer")}</Styled.FilterFormLabel>
                        <CheckboxInput name="payerEmployee" label={t("fufi.regulationsFilters.payerEmployee")} />
                        <CheckboxInput name="payerEmployer" label={t("fufi.regulationsFilters.payerEmployer")} />
                    </Stack>

                    <Stack>
                        <Styled.FilterFormLabel>{t("fufi.regulationsFilters.paymentsType")}</Styled.FilterFormLabel>
                        <CheckboxInput
                            name="paymentTypeRegular"
                            label={t("fufi.regulationsFilters.paymentTypeRegular")}
                        />
                        <CheckboxInput name="paymentTypeExtra" label={t("fufi.regulationsFilters.paymentTypeExtra")} />
                    </Stack>

                    <Stack>
                        <Styled.FilterFormLabel htmlFor="from">
                            {t("fufi.regulationsFilters.periodPayments")}
                        </Styled.FilterFormLabel>
                        <Stack sx={{ gap: 5, flexDirection: { xs: "column", sm: "row" } }}>
                            <DatePicker name="from" placeholder={t("fufi.regulationsFilters.dateFrom")} />
                            <DatePicker name="to" placeholder={t("fufi.regulationsFilters.dateTo")} />
                        </Stack>
                    </Stack>
                </Styled.VerticalStackWithGap>
            </form>
        </FormProvider>
    );
};
